import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SmartImage from "./SmartImage";
import { pageHref } from "../lib/format";

/**
 * Migrated alt text is usually just the upload filename ("XEB-TOY-O6",
 * "pexels-photo-12138615", "Screenshot 2025-08-23 113059"). Only surface it as
 * a visible caption when it actually reads like a caption.
 */
function prettyCaption(alt) {
  const s = String(alt || "").trim();
  if (!s || s.length < 6 || s.length > 120) return "";
  if (!s.includes(" ")) return "";                       // slug / filename
  if (/^screenshot\b/i.test(s)) return "";
  if (/(^|[\s-_])(pexels|unsplash|istock|shutterstock|img|dsc|photo)[\s-_]?\d/i.test(s)) return "";
  if (/\d{3,}/.test(s)) return "";                       // dimension / id dumps
  if (/\.(jpe?g|png|webp|gif|svg)$/i.test(s)) return "";
  const words = s.split(/\s+/);
  if (words.length < 2) return "";
  return s;
}

function isInternal(href) {
  if (!href) return false;
  return href.startsWith("/") || href.includes("nqsprings.com.au");
}

function hrefToPath(href, slug) {
  if (slug) return pageHref(slug);
  if (!href) return "/";
  if (href.startsWith("/")) return href;
  try {
    const u = new URL(href);
    return u.pathname === "/" ? "/" : u.pathname.replace(/\/$/, "");
  } catch {
    return "/";
  }
}

/**
 * Renders the migrated content blocks. Consecutive images are grouped into a
 * grid so image-heavy pages read as galleries rather than a long vertical strip.
 */
export default function BlockRenderer({ blocks = [], onImageClick }) {
  if (!blocks.length) return null;

  const groups = [];
  blocks.forEach((b) => {
    const last = groups[groups.length - 1];
    if (b.type === "image") {
      if (last && last.type === "images") last.items.push(b);
      else groups.push({ type: "images", items: [b] });
    } else if (b.type === "button") {
      if (last && last.type === "buttons") last.items.push(b);
      else groups.push({ type: "buttons", items: [b] });
    } else {
      groups.push({ type: "single", block: b });
    }
  });

  return (
    <div className="prose-nq space-y-6" data-testid="block-renderer">
      {groups.map((g, gi) => {
        if (g.type === "images") {
          const items = g.items;
          if (items.length === 1) {
            return (
              <button
                key={gi}
                type="button"
                onClick={() => onImageClick && onImageClick(items[0].url)}
                className="card-nq block w-full cursor-zoom-in"
                data-testid="content-image"
              >
                <SmartImage src={items[0].url} alt={items[0].alt} ratio="aspect-[16/9]" fit="contain" />
              </button>
            );
          }
          return (
            <div
              key={gi}
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
              data-testid="content-image-grid"
            >
              {items.map((im, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onImageClick && onImageClick(im.url)}
                  className="card-nq cursor-zoom-in"
                  data-testid="content-image"
                >
                  <SmartImage src={im.url} alt={im.alt} ratio="aspect-[4/3]" fit="contain" />
                  {prettyCaption(im.alt) && (
                    <p className="truncate px-3 py-2 text-left text-xs text-[color:var(--nq-stone)]">
                      {prettyCaption(im.alt)}
                    </p>
                  )}
                </button>
              ))}
            </div>
          );
        }

        if (g.type === "buttons") {
          return (
            <div key={gi} className="flex flex-wrap gap-3">
              {g.items.map((b, i) =>
                isInternal(b.href) || b.slug ? (
                  <Link
                    key={i}
                    to={hrefToPath(b.href, b.slug)}
                    className={i === 0 ? "btn-gold" : "btn-outline-nq"}
                    data-testid="content-button"
                  >
                    {b.text} <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <a
                    key={i}
                    href={b.href}
                    target="_blank"
                    rel="noreferrer"
                    className={i === 0 ? "btn-gold" : "btn-outline-nq"}
                    data-testid="content-button"
                  >
                    {b.text} <ArrowRight className="h-4 w-4" />
                  </a>
                )
              )}
            </div>
          );
        }

        const b = g.block;
        switch (b.type) {
          case "heading": {
            const lvl = Math.min(6, Math.max(2, b.level || 2));
            const Tag = `h${lvl}`;
            const anchor = String(b.text || "")
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-|-$/g, "");
            const size =
              lvl <= 2
                ? "text-2xl md:text-3xl font-bold"
                : lvl === 3
                ? "text-xl md:text-2xl font-semibold"
                : "text-lg font-semibold";
            return (
              <Tag
                key={gi}
                id={anchor || undefined}
                className={`${size} scroll-mt-28 pt-2 text-[color:var(--nq-sand)]`}
                data-testid="content-heading"
              >
                {b.text}
              </Tag>
            );
          }
          case "paragraph":
            return (
              <p key={gi} data-testid="content-paragraph">
                {b.text}
              </p>
            );
          case "list":
            return (
              <ul key={gi} className="space-y-2" data-testid="content-list">
                {(b.items || []).map((li, i) => (
                  <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed">
                    <Check className="mt-1 h-4 w-4 shrink-0 gold-text" />
                    <span style={{ color: "rgba(233,226,210,0.82)" }}>{li}</span>
                  </li>
                ))}
              </ul>
            );
          case "quote":
            return (
              <blockquote
                key={gi}
                className="rounded-r-lg border-l-2 py-2 pl-4 italic"
                style={{ borderColor: "var(--nq-gold)", background: "rgba(198,162,74,0.06)" }}
                data-testid="content-quote"
              >
                {b.text}
              </blockquote>
            );
          case "table":
            return (
              <div key={gi} className="card-nq overflow-x-auto" data-testid="content-table">
                <table className="w-full text-sm">
                  <tbody>
                    {(b.rows || []).map((row, ri) => (
                      <tr
                        key={ri}
                        className="border-b last:border-0"
                        style={{ borderColor: "rgba(255,255,255,0.07)" }}
                      >
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className={`px-3 py-2 ${ri === 0 ? "font-semibold gold-text" : "text-[color:var(--nq-stone)]"}`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
